/**
 * The browser-side identity boundary (M2-01, AUTH-001). The Firebase implementation lives in
 * `firebase-client.ts`; tests use a fake. Nothing here authorises anything: the Worker
 * verifies the ID token on every protected request.
 */
export type IdentityUser = { uid: string; email: string | null; emailVerified: boolean };

export interface IdentityClient {
  /** Fires on sign-in, sign-out, token refresh and when a refresh token stops working. */
  onUserChanged(listener: (user: IdentityUser | null) => void): () => void;
  /** Current ID token, or null when signed out. `forceRefresh` fetches a new one. */
  getIdToken(forceRefresh?: boolean): Promise<string | null>;
  signInWithGoogle(): Promise<void>;
  signInWithPassword(email: string, password: string): Promise<void>;
  /** Creates the account and sends a verification email. */
  createAccount(email: string, password: string): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  sendSignInLink(email: string): Promise<void>;
  isSignInLink(url: string): boolean;
  completeSignInLink(email: string, url: string): Promise<void>;
  resendVerification(): Promise<void>;
  /** Re-reads the account (e.g. after verifying email) and refreshes the token. */
  reloadUser(): Promise<void>;
  signOut(): Promise<void>;
}

const MESSAGES: Record<string, string> = {
  // Firebase's email-enumeration protection returns one code for every bad-credential case.
  'auth/invalid-credential': 'The email or password is incorrect.',
  'auth/wrong-password': 'The email or password is incorrect.',
  'auth/user-not-found': 'The email or password is incorrect.',
  'auth/invalid-email': 'Enter a valid email address.',
  'auth/missing-password': 'Enter your password.',
  'auth/weak-password': 'Choose a password that meets every rule shown.',
  'auth/password-does-not-meet-requirements': 'Choose a password that meets every rule shown.',
  'auth/email-already-in-use':
    'An account may already use this email. Try signing in or resetting your password.',
  'auth/popup-closed-by-user': 'The Google window closed before sign-in finished.',
  'auth/cancelled-popup-request': 'The Google window closed before sign-in finished.',
  'auth/popup-blocked': 'Your browser blocked the Google window. Allow pop-ups and try again.',
  'auth/account-exists-with-different-credential':
    'This email already uses a different sign-in method. Sign in that way instead.',
  'auth/invalid-action-code': 'This sign-in link is invalid or has already been used.',
  'auth/expired-action-code': 'This sign-in link has expired. Request a new one.',
  'auth/user-disabled': 'This account is disabled.',
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
  'auth/network-request-failed': 'Could not reach the sign-in service. Check your connection.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled.',
};

/** User-facing message for an identity error. Never echoes provider internals. */
export function describeAuthError(error: unknown): string {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
  return MESSAGES[code] ?? 'Sign-in did not complete. Try again.';
}

export const MIN_PASSWORD_LENGTH = 8;
